import { Spin } from 'antd';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { useApplicationByReferenceNumber } from 'queries';
import type { ApplicationDetailsFormatted } from 'queries/applications/useApplicationByReferenceNumber/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { OoCaseFormFooter } from './OoCaseFormFooter';
import { isOoPropertyFormEditable } from './Property';
import { OoCaseSidebar } from './OoCaseSidebar';
import { useOoApplicationViewData } from './useOoApplicationViewData';
import { useOoFormMappedRoutes } from './useOoFormMappedRoutes';
import { useOoSectionNavigation } from './useOoSectionNavigation';

interface OoApplicationViewPageContentProps {
  application: ApplicationDetailsFormatted;
}

function OoApplicationViewPageContent({ application }: OoApplicationViewPageContentProps) {
  const viewData = useOoApplicationViewData(application);
  const { formSectionParam, updateSectionInUrl } = useOoSectionNavigation(viewData.sidebarContext);
  const [openBankDetailsInEditMode, setOpenBankDetailsInEditMode] = useState(false);

  const handleGoToEvidence = useCallback(() => {
    updateSectionInUrl(CaseSidebarItemId.EVIDENCE);
  }, [updateSectionInUrl]);

  const handleGoToBankDetails = useCallback(() => {
    setOpenBankDetailsInEditMode(true);
    updateSectionInUrl(CaseSidebarItemId.BANK_DETAILS);
  }, [updateSectionInUrl]);

  const handleOpenBankDetailsInEditModeConsumed = useCallback(() => {
    setOpenBankDetailsInEditMode(false);
  }, []);

  const routeContext = useMemo(
    () => ({
      ...viewData,
      openBankDetailsInEditMode,
      onOpenBankDetailsInEditModeConsumed: handleOpenBankDetailsInEditModeConsumed,
      onGoToEvidence: handleGoToEvidence,
      onGoToBankDetails: handleGoToBankDetails,
    }),
    [
      viewData,
      openBankDetailsInEditMode,
      handleOpenBankDetailsInEditModeConsumed,
      handleGoToEvidence,
      handleGoToBankDetails,
    ],
  );

  const { FormPage, sectionTitleKey, currentSection, previousSection, nextSection, previousLabel, nextLabel } =
    useOoFormMappedRoutes(routeContext);

  const isPropertyFormEditable =
    currentSection === CaseSidebarItemId.PROPERTY && isOoPropertyFormEditable(viewData.case);

  useEffect(() => {
    if (currentSection !== CaseSidebarItemId.BANK_DETAILS) {
      setOpenBankDetailsInEditMode(false);
    }
  }, [currentSection]);

  return (
    <div className='-mt-16 flex h-full min-h-0 bg-gray-100'>
      <div className='flex h-full min-h-0 shrink-0'>
        <OoCaseSidebar
          application={application}
          caseData={viewData.case}
          isCaseLoading={viewData.isCaseLoading}
          currentSection={formSectionParam ?? undefined}
          context={viewData.sidebarContext}
        />
      </div>
      <div className='flex min-h-0 flex-1 gap-8 p-6 pb-4 pl-8'>
        <div className='relative flex h-full grow flex-col pt-4'>
          <h2 className='mt-0'>
            <FormattedMessage id={sectionTitleKey} />
          </h2>
          <div id='form-scroll-container' className='custom-scrollbar mb-16 grow overflow-auto pb-4 pl-2 pr-4 pt-2'>
            {FormPage ? <FormPage /> : null}
          </div>
          {!isPropertyFormEditable && (
            <OoCaseFormFooter
              previousSection={previousSection}
              nextSection={nextSection}
              previousLabel={previousLabel}
              nextLabel={nextLabel}
              onNavigate={updateSectionInUrl}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export const OoApplicationViewPage = () => {
  const params = useParams();
  const { data: application, isLoading } = useApplicationByReferenceNumber(params?.id || '');

  if (isLoading || !application) {
    return (
      <div className='-mt-16 flex h-full min-h-0 items-center justify-center bg-gray-100'>
        <Spin size='large' />
      </div>
    );
  }

  return <OoApplicationViewPageContent application={application} />;
};
